import { fitsWithin, volumetricKg } from "./geometry.ts";
import type { Dimensions, Pence } from "./types.ts";
import { round } from "./units.ts";

/** One price band of a carrier service. Size limits apply to the parcel's sorted dimensions. */
export interface Band {
  code: string;
  name: string;
  /** Longest side limit */
  maxLengthCm: number;
  /** Middle side limit */
  maxWidthCm: number;
  /** Shortest side limit */
  maxHeightCm: number;
  /** Limit on chargeable weight (the higher of actual and volumetric) */
  maxWeightKg: number;
  pricePence: Pence;
}

export interface Service {
  code: string;
  name: string;
  carrier: string;
  /** cm³ per kg, e.g. 5000; null when the service does not charge volumetric weight */
  volumetricDivisor: number | null;
  bands: Band[];
}

/** A dated, versioned price list. Reports record `id@version` of every card used. */
export interface RateCard {
  id: string;
  version: string;
  /** ISO date the prices take effect */
  effectiveFrom: string;
  /** Where the prices came from: published tariff, client invoice, or "SAMPLE" */
  source: string;
  services: Service[];
}

const EPS = 1e-9;

/** Longest, middle, shortest. */
export function sortedSides(d: Dimensions): [number, number, number] {
  const s = [d.lengthCm, d.widthCm, d.heightCm].sort((a, b) => b - a);
  return [s[0]!, s[1]!, s[2]!];
}

export function volumetricWeightKg(d: Dimensions, divisor: number | null): number | null {
  if (!divisor) return null;
  return round(volumetricKg([d.lengthCm, d.widthCm, d.heightCm], divisor), 3);
}

export function chargeableWeightKg(d: Dimensions, actualKg: number, divisor: number | null): number {
  const vol = volumetricWeightKg(d, divisor);
  return vol === null ? actualKg : Math.max(actualKg, vol);
}

export function fitsBand(d: Dimensions, chargeableKg: number, band: Band): boolean {
  return fitsWithin([d.lengthCm, d.widthCm, d.heightCm], chargeableKg, [band.maxLengthCm, band.maxWidthCm, band.maxHeightCm], band.maxWeightKg);
}

/** How far a parcel exceeds a band on each limit; empty when it fits. */
export function excessOver(d: Dimensions, chargeableKg: number, band: Band) {
  const [l, w, h] = sortedSides(d);
  const out: { lengthCm?: number; widthCm?: number; heightCm?: number; weightKg?: number } = {};
  if (l > band.maxLengthCm + EPS) out.lengthCm = round(l - band.maxLengthCm, 2);
  if (w > band.maxWidthCm + EPS) out.widthCm = round(w - band.maxWidthCm, 2);
  if (h > band.maxHeightCm + EPS) out.heightCm = round(h - band.maxHeightCm, 2);
  if (chargeableKg > band.maxWeightKg + EPS) out.weightKg = round(chargeableKg - band.maxWeightKg, 3);
  return out;
}

export interface PriceResult {
  band: Band;
  chargeableKg: number;
  volumetricKg: number | null;
}

/** The cheapest band of the service that the parcel fits, or null if none does. */
export function priceParcel(d: Dimensions, actualKg: number, service: Service): PriceResult | null {
  const volumetricKg = volumetricWeightKg(d, service.volumetricDivisor);
  const chargeableKg = volumetricKg === null ? actualKg : Math.max(actualKg, volumetricKg);
  let best: Band | null = null;
  for (const band of service.bands) {
    if (fitsBand(d, chargeableKg, band) && (!best || band.pricePence < best.pricePence)) best = band;
  }
  return best ? { band: best, chargeableKg, volumetricKg } : null;
}

export function findService(cards: RateCard[], code: string): { card: RateCard; service: Service } | null {
  for (const card of cards) {
    const service = card.services.find((s) => s.code === code);
    if (service) return { card, service };
  }
  return null;
}

/** Basic sanity checks a rate card must pass before use. */
export function validateRateCard(card: RateCard): string[] {
  const problems: string[] = [];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(card.effectiveFrom)) problems.push("effectiveFrom must be YYYY-MM-DD");
  const codes = new Set<string>();
  for (const s of card.services) {
    if (codes.has(s.code)) problems.push(`duplicate service code ${s.code}`);
    codes.add(s.code);
    if (s.bands.length === 0) problems.push(`service ${s.code} has no bands`);
    for (const b of s.bands) {
      if (!Number.isInteger(b.pricePence) || b.pricePence < 0) problems.push(`${s.code}/${b.code}: price must be whole pence`);
      if (!(b.maxLengthCm >= b.maxWidthCm && b.maxWidthCm >= b.maxHeightCm))
        problems.push(`${s.code}/${b.code}: size limits must run longest, middle, shortest`);
    }
  }
  return problems;
}
